// src/components/BucksAdminOrderManage.jsx

import React, { useState, useEffect, useRef } from 'react';
import '../stylings/BucksAdminOrderManage.css';
import BucksAdminOrderDetails from './BucksAdminOrderDetails';

// Define API endpoints
const GET_ORDERS_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-online-orders-get';
const UPDATE_ORDER_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-order-update';

function BucksAdminOrderManage() {
  /* ────────────────────── STATE ────────────────────── */
  // Orders data
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [detailsPanel, setDetailsPanel] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [orderUpdateData, setOrderUpdateData] = useState({
    status: '',
    trackingNumber: '',
    notes: ''
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  
  // Refs
  const searchTimer = useRef(null);
  const detailsPanelRef = useRef(null);
  
  /* ────────────────────── CONSTANTS ────────────────────── */
  const ORDER_STATUSES = [
    'Pending',
    'Processing',
    'Shipped',
    'Delivered',
    'Cancelled',
    'Refunded'
  ];
  
  const STATUS_COLORS = {
    Pending: 'status-pending',
    Processing: 'status-processing',
    Shipped: 'status-shipped',
    Delivered: 'status-delivered',
    Cancelled: 'status-cancelled',
    Refunded: 'status-refunded'
  };
  
  /* ────────────────────── EFFECTS ────────────────────── */
  // Fetch orders when component mounts or filters change
  useEffect(() => {
    fetchOrders(pagination.page, searchTerm, statusFilter, dateRange);
  }, [statusFilter, pagination.page]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Close details panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (detailsPanel && detailsPanelRef.current && !detailsPanelRef.current.contains(event.target)) {
        setDetailsPanel(false);
      }
    }
    
    if (detailsPanel) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
    return () => {};
  }, [detailsPanel]);
  
  /* ────────────────────── API CALLS ────────────────────── */
  // Fetch orders from API
  const fetchOrders = async (page = 1, search = '', status = 'all', dates = { from: '', to: '' }) => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.page_size);
      
      if (search) {
        params.append('search', search);
      }
      
      if (status !== 'all') {
        params.append('status', status);
      }
      
      if (dates.from) {
        params.append('date_from', dates.from);
      }
      
      if (dates.to) {
        params.append('date_to', dates.to);
      }
      
      // Make API call
      const response = await fetch(`${GET_ORDERS_URL}?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      
      setOrders(data.orders || []);
      setPagination(data.pagination || {
        page: page,
        page_size: 10,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      });
      
    } catch (error) {
      console.error('Error fetching orders:', error);
      setNotification({
        show: true,
        message: `Error loading orders: ${error.message}`,
        type: 'error'
      });
      setOrders([]);
      
      // Set default pagination on error
      setPagination({
        page: 1,
        page_size: 10,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Update order status
  const updateOrderStatus = async (orderId, updateData) => {
    try {
      setLoading(true);
      
      // Make API call to update order
      const response = await fetch(UPDATE_ORDER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: orderId,
          ...updateData
        })
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      // Refresh orders list
      await fetchOrders(pagination.page, searchTerm, statusFilter, dateRange);
      
      // Update selected order if it's open
      if (selectedOrder && selectedOrder.id === orderId) {
        const updatedOrder = orders.find(order => order.id === orderId);
        if (updatedOrder) {
          setSelectedOrder({
            ...updatedOrder,
            status: updateData.status,
            tracking_number: updateData.trackingNumber,
            notes: updateData.notes,
            updated_at: new Date().toISOString()
          });
        }
      }
      
      setNotification({
        show: true,
        message: `Order ${orderId} has been updated successfully.`,
        type: 'success'
      });
      
      // Hide notification after 3 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error updating order:', error);
      setNotification({
        show: true,
        message: `Error updating order: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
      setShowUpdateModal(false);
    }
  };
  
  /* ────────────────────── EVENT HANDLERS ────────────────────── */
  // Handle search input changes with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce to avoid too many API calls while typing
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchOrders(1, value, statusFilter, dateRange);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
  };
  
  // Handle status filter changes
  const handleStatusChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  // Handle date range changes
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const newDateRange = { ...dateRange, [name]: value };
    setDateRange(newDateRange);
    
    // Only trigger search if both dates are set or both are empty
    if ((newDateRange.from && newDateRange.to) || (!newDateRange.from && !newDateRange.to)) {
      fetchOrders(1, searchTerm, statusFilter, newDateRange);
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };
  
  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
  
  // Toggle order details expansion
  const toggleOrderExpansion = (e, orderId) => {
    // Stop event propagation to prevent row click handler from firing
    e.stopPropagation();
    
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
    } else {
      setExpandedOrderId(orderId);
    }
  };
  
  // Handle row click to open order details
  const handleRowClick = (order) => {
    openOrderDetails(order);
  };
  
  // Open order details panel
  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setDetailsPanel(true);
  };
  
  // Close order details panel
  const closeOrderDetails = () => {
    setDetailsPanel(false);
    // Wait for animation to complete before clearing the selected order
    setTimeout(() => {
      setSelectedOrder(null);
    }, 300);
  };
  
  // Open update modal
  const openUpdateModal = (e, order) => {
    // If event is provided, stop propagation to prevent row click
    if (e) {
      e.stopPropagation();
    }
    
    setSelectedOrder(order);
    setOrderUpdateData({
      status: order.status,
      trackingNumber: order.tracking_number || '',
      notes: order.admin_notes || ''
    });
    setShowUpdateModal(true);
  };
  
  // Handle update modal form changes
  const handleUpdateFormChange = (e) => {
    const { name, value } = e.target;
    setOrderUpdateData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle update form submission
  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    if (selectedOrder) {
      updateOrderStatus(selectedOrder.id, orderUpdateData);
    }
  };
  
  // Handle button click in the row without triggering row click
  const handleButtonClick = (e) => {
    e.stopPropagation();
  };
  
  // Handle export functionality
  const handleExport = async () => {
    try {
      // You can implement CSV export here
      setNotification({
        show: true,
        message: 'Export functionality coming soon!',
        type: 'warning'
      });
      
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    } catch (error) {
      console.error('Export error:', error);
      setNotification({
        show: true,
        message: 'Error exporting orders',
        type: 'error'
      });
    }
  };
  
  /* ────────────────────── UTILITY FUNCTIONS ────────────────────── */
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };
  
  // Get status badge class
  const getStatusBadgeClass = (status) => {
    return STATUS_COLORS[status] || 'status-pending';
  };
  
  /* ────────────────────── RENDER ────────────────────── */
  return (
    <div className="bucks-admin-orders-section">
      {/* Notification */}
      {notification.show && (
        <div className={`bucks-admin-orders-notification ${notification.type}`}>
          <span className="bucks-admin-orders-notification-message">{notification.message}</span>
          <button 
            className="bucks-admin-orders-close-notification" 
            onClick={() => setNotification({ show: false, message: '', type: '' })}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Header - Search and Filters */}
      <div className="bucks-admin-orders-actions">
        <div className="bucks-admin-orders-search-filter-container">
          <div className="bucks-admin-orders-search-container">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search orders by ID, customer name or email..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="bucks-admin-orders-search-input"
            />
          </div>
          
          <div className="bucks-admin-orders-filter-container">
            <div className="bucks-admin-orders-filter-group">
              <label htmlFor="status-filter" className="bucks-admin-orders-filter-label">Status:</label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={handleStatusChange}
                className="bucks-admin-orders-status-filter"
              >
                <option value="all">All Statuses</option>
                {ORDER_STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            
            <div className="bucks-admin-orders-filter-group">
              <label className="bucks-admin-orders-filter-label">Date Range:</label>
              <div className="bucks-admin-orders-date-inputs">
                <input
                  type="date"
                  name="from"
                  value={dateRange.from}
                  onChange={handleDateChange}
                  className="bucks-admin-orders-date-input"
                  placeholder="From"
                />
                <span className="bucks-admin-orders-date-separator">to</span>
                <input
                  type="date"
                  name="to"
                  value={dateRange.to}
                  onChange={handleDateChange}
                  className="bucks-admin-orders-date-input"
                  placeholder="To"
                />
              </div>
            </div>
          </div>
        </div>
        
        <button className="bucks-admin-orders-export-btn" onClick={handleExport}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Export Orders
        </button>
      </div>
      
      {/* Main Content */}
      {loading ? (
        <div className="bucks-admin-orders-loading">
          <div className="bucks-admin-orders-loading-spinner"></div>
          <span>Loading orders...</span>
        </div>
      ) : (
        <>
          {/* Orders Table */}
          <div className="bucks-admin-orders-table-container">
            <table className="bucks-admin-orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map(order => (
                    <React.Fragment key={order.id}>
                      <tr 
                        className={`${expandedOrderId === order.id ? 'expanded' : ''} bucks-admin-orders-clickable-row`}
                        onClick={() => handleRowClick(order)}
                      >
                        <td className="bucks-admin-orders-id-cell">
                          <div className="bucks-admin-orders-id-content">
                            <button 
                              className="bucks-admin-orders-expand-btn"
                              onClick={(e) => toggleOrderExpansion(e, order.id)}
                              aria-label={expandedOrderId === order.id ? "Collapse order details" : "Expand order details"}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {expandedOrderId === order.id ? (
                                  <polyline points="18 15 12 9 6 15"></polyline>
                                ) : (
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                )}
                              </svg>
                            </button>
                            {order.id}
                          </div>
                        </td>
                        <td>{formatDate(order.created_at)}</td>
                        <td className="bucks-admin-orders-customer-cell">
                          <div className="bucks-admin-orders-customer-name">{order.customer?.name || 'Unknown'}</div>
                          <div className="bucks-admin-orders-customer-email">{order.customer?.email || ''}</div>
                        </td>
                        <td className="bucks-admin-orders-total-cell">{formatCurrency(order.total)}</td>
                        <td>
                          <span className={`bucks-admin-orders-status-badge ${getStatusBadgeClass(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td>{order.payment_method}</td>
                        <td onClick={handleButtonClick}>
                          <div className="bucks-admin-orders-actions-cell">
                            <button 
                              className="bucks-admin-orders-view-btn" 
                              onClick={() => openOrderDetails(order)}
                              title="View Order Details"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </button>
                            <button 
                              className="bucks-admin-orders-update-btn" 
                              onClick={(e) => openUpdateModal(e, order)}
                              title="Update Order Status"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button 
                              className="bucks-admin-orders-print-btn" 
                              onClick={(e) => { e.stopPropagation(); window.print(); }}
                              title="Print Order"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 6 2 18 2 18 9"></polyline>
                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                                <rect x="6" y="14" width="12" height="8"></rect>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedOrderId === order.id && (
                        <tr className="bucks-admin-orders-expanded-row">
                          <td colSpan="7">
                            <div className="bucks-admin-orders-expanded-content">
                              <div className="bucks-admin-orders-expanded-section">
                                <h4>Order Items</h4>
                                <table className="bucks-admin-orders-items-table">
                                  <thead>
                                    <tr>
                                      <th>Product</th>
                                      <th>Price</th>
                                      <th>Quantity</th>
                                      <th>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {order.items?.map(item => (
                                      <tr key={item.id}>
                                        <td>{item.name}</td>
                                        <td>{formatCurrency(item.price)}</td>
                                        <td>{item.quantity}</td>
                                        <td>{formatCurrency(item.total)}</td>
                                      </tr>
                                    )) || (
                                      <tr>
                                        <td colSpan="4">No items found</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                              
                              <div className="bucks-admin-orders-expanded-footer">
                                <div className="bucks-admin-orders-expanded-address">
                                  <h4>Shipping Address</h4>
                                  <p>{order.shipping_address?.street || 'No address provided'}</p>
                                  <p>{order.shipping_address?.city || ''}, {order.shipping_address?.state || ''} {order.shipping_address?.zipcode || ''}</p>
                                </div>
                                <div className="bucks-admin-orders-expanded-summary">
                                  <div className="bucks-admin-orders-summary-row">
                                    <span>Subtotal:</span>
                                    <span>{formatCurrency(order.subtotal)}</span>
                                  </div>
                                  <div className="bucks-admin-orders-summary-row">
                                    <span>Tax:</span>
                                    <span>{formatCurrency(order.tax)}</span>
                                  </div>
                                  <div className="bucks-admin-orders-summary-row">
                                    <span>Shipping:</span>
                                    <span>{formatCurrency(order.shipping)}</span>
                                  </div>
                                  <div className="bucks-admin-orders-summary-row bucks-admin-orders-total-row">
                                    <span>Total:</span>
                                    <span>{formatCurrency(order.total)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="bucks-admin-orders-no-results">
                      No orders found matching your search criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="bucks-admin-orders-pagination">
              <button 
                className="bucks-admin-orders-pagination-btn" 
                disabled={!pagination.has_prev}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </button>
              <span className="bucks-admin-orders-pagination-info">
                Page {pagination.page} of {pagination.total_pages} ({pagination.total_count} total orders)
              </span>
              <button 
                className="bucks-admin-orders-pagination-btn" 
                disabled={!pagination.has_next}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Order Details Side Panel - Using the new component */}
      <div ref={detailsPanelRef}>
        <BucksAdminOrderDetails
          selectedOrder={selectedOrder}
          isOpen={detailsPanel}
          onClose={closeOrderDetails}
          onUpdate={openUpdateModal}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          getStatusBadgeClass={getStatusBadgeClass}
        />
      </div>
      
      {/* Order Update Modal */}
      {showUpdateModal && selectedOrder && (
        <div className="bucks-admin-orders-modal-overlay" onClick={() => setShowUpdateModal(false)}>
          <div className="bucks-admin-orders-modal-container" onClick={e => e.stopPropagation()}>
            <div className="bucks-admin-orders-modal-header">
              <h2>Update Order {selectedOrder.id}</h2>
              <button 
                className="bucks-admin-orders-modal-close" 
                onClick={() => setShowUpdateModal(false)}
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleUpdateSubmit} className="bucks-admin-orders-modal-form">
              <div className="bucks-admin-orders-modal-form-group">
                <label htmlFor="status">Order Status</label>
                <select
                  id="status"
                  name="status"
                  value={orderUpdateData.status}
                  onChange={handleUpdateFormChange}
                  required
                >
                  {ORDER_STATUSES.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div className="bucks-admin-orders-modal-form-group">
                <label htmlFor="trackingNumber">Tracking Number</label>
                <input
                  type="text"
                  id="trackingNumber"
                  name="trackingNumber"
                  value={orderUpdateData.trackingNumber}
                  onChange={handleUpdateFormChange}
                  placeholder="Enter tracking number (optional)"
                />
              </div>
              
              <div className="bucks-admin-orders-modal-form-group">
                <label htmlFor="notes">Admin Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={orderUpdateData.notes}
                  onChange={handleUpdateFormChange}
                  placeholder="Add notes about this order (optional)"
                  rows="4"
                ></textarea>
              </div>
              
              <div className="bucks-admin-orders-modal-actions">
                <button 
                  type="button" 
                  className="bucks-admin-orders-modal-cancel" 
                  onClick={() => setShowUpdateModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="bucks-admin-orders-modal-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      <span>Updating...</span>
                    </>
                  ) : (
                    'Update Order'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BucksAdminOrderManage;